import { Instagram, Music2, Linkedin } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';

interface BoostPassPromoProps {
  userName?: string;
  isPreview?: boolean;
}

const BoostPassPromo = ({
  userName = 'Voyager',
  isPreview = false
}: BoostPassPromoProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleBoostPass = async (tier: 'premium_economy' | 'first_class', duration: string, price: number) => {
    if (!isPreview) return; // Only work in preview mode
    
    if (!user) {
      navigate('/auth');
      return;
    }

    try {
      toast({
        title: "Redirecting to payment...",
        description: "Please wait while we prepare your checkout session."
      });

      const { data, error } = await supabase.functions.invoke('create-boost-checkout', {
        body: {
          tier,
          duration: `${duration}-Day` // Format duration as "3-Day", "7-Day", or "14-Day"
        }
      });

      if (error) throw error;
      
      if (data?.url) {
        toast({
          title: "Redirecting to Stripe...",
          description: "You'll be redirected to complete your payment."
        });
        setTimeout(() => {
          window.location.href = data.url;
        }, 500);
      }
    } catch (error) {
      console.error('Error creating boost checkout:', error);
      toast({
        title: "Error",
        description: "Failed to start checkout. Please try again.",
        variant: "destructive"
      });
    }
  };
  return <>
      <style>{`
        @media (max-width: 640px) {
          .email-container {
            padding: 20px 16px !important;
          }
          .email-logo {
            width: 150px !important;
          }
          .email-heading {
            font-size: 32px !important;
            margin: 8px 0 16px !important;
          }
          .email-text {
            font-size: 14px !important;
            line-height: 22px !important;
          }
          .email-feature-box {
            padding: 20px 16px !important;
            margin: 24px 0 !important;
          }
          .email-feature-heading {
            font-size: 20px !important;
            margin: 0 0 12px !important;
          }
          .email-pricing-box {
            padding: 16px !important;
            margin: 20px 0 !important;
          }
          .email-button {
            padding: 12px 20px !important;
            font-size: 14px !important;
          }
          .email-social-icon {
            width: 24px !important;
            height: 24px !important;
          }
          .comparison-table th,
          .comparison-table td {
            padding: 8px 6px !important;
            font-size: 11px !important;
          }
          .comparison-table th {
            font-size: 10px !important;
          }
          .comparison-table-heading {
            font-size: 16px !important;
          }
        }
      `}</style>
      <div style={{
      backgroundColor: '#f0fafa',
      minHeight: '100vh',
      padding: '40px 20px',
      fontFamily: "Montserrat, -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif"
    }}>
        <div className="email-container" style={{
        backgroundColor: '#ffffff',
        margin: '0 auto',
        padding: '40px 20px',
        borderRadius: '8px',
        maxWidth: '600px'
      }}>
        <div style={{
          textAlign: 'center',
          marginBottom: '0'
        }}>
          <img src="/lovable-uploads/odyvoy-logo.png" alt="OdyVoy Logo" className="email-logo" style={{
            width: '250px',
            height: 'auto',
            margin: '0 auto'
          }} />
        </div>
        <h1 className="email-heading" style={{
          color: '#1a9a9a',
          fontSize: '28px',
          fontWeight: 'bold',
          margin: '8px 0 24px',
          padding: '0',
          textAlign: 'center'
        }}>
          Unlock Premium Features Today! 🚀
        </h1>
        
        <p className="email-text" style={{
          color: '#333',
          fontSize: '16px',
          lineHeight: '24px',
          margin: '16px 0'
        }}>
          Hi {userName},
        </p>
        
        <p className="email-text" style={{
          color: '#333',
          fontSize: '16px',
          lineHeight: '24px',
          margin: '16px 0'
        }}>
          Planning an important trip and need premium features right away? OdyVoy Boost Passes give you instant access to our most powerful tools—without a long-term commitment.
        </p>
        
        <div className="email-feature-box" style={{
          background: 'linear-gradient(135deg, #1a9a9a, #6dd4d4)',
          borderRadius: '8px',
          padding: '24px',
          margin: '32px 0'
        }}>
          <h2 className="email-feature-heading" style={{
            color: '#ffffff',
            fontSize: '24px',
            fontWeight: 'bold',
            margin: '0 0 16px',
            padding: '0'
          }}>
            Why Get a Boost Pass?
          </h2>
          
          <ul style={{
            margin: '16px 0',
            padding: '0 0 0 20px'
          }}>
            <li className="email-text" style={{
              color: '#ffffff',
              fontSize: '16px',
              lineHeight: '28px',
              margin: '8px 0'
            }}>
              ⚡ Instant Access To All Premium Features
            </li>
            <li className="email-text" style={{
              color: '#ffffff',
              fontSize: '16px',
              lineHeight: '28px',
              margin: '8px 0'
            }}>
              💡 Perfect For Short Trips Or One-Time Use
            </li>
            <li className="email-text" style={{
              color: '#ffffff',
              fontSize: '16px',
              lineHeight: '28px',
              margin: '8px 0'
            }}>
              🎯 No Subscription Required
            </li>
            <li className="email-text" style={{
              color: '#ffffff',
              fontSize: '16px',
              lineHeight: '28px',
              margin: '8px 0'
            }}>💰 Pay Only For What You Need As Long As You Need</li>
          </ul>
        </div>

        <div style={{
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          padding: '20px',
          margin: '24px 0',
          overflowX: 'auto'
        }}>
          <h3 className="email-feature-heading comparison-table-heading" style={{
            color: '#1a9a9a',
            fontSize: '20px',
            fontWeight: 'bold',
            margin: '0 0 20px',
            textAlign: 'center'
          }}>
            Compare Boost Pass Tiers
          </h3>
          
          <table className="comparison-table" style={{
            width: '100%',
            borderCollapse: 'collapse',
            backgroundColor: '#ffffff',
            borderRadius: '8px',
            overflow: 'hidden'
          }}>
            <thead>
              <tr style={{
                backgroundColor: '#1a9a9a'
              }}>
                <th style={{
                  padding: '16px',
                  textAlign: 'left',
                  color: '#ffffff',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  borderBottom: '2px solid #e6e6e6'
                }}>Features</th>
                <th style={{
                  padding: '16px',
                  textAlign: 'center',
                  color: '#ffffff',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  borderBottom: '2px solid #e6e6e6'
                }}>Economy Class Pack</th>
                <th style={{
                  padding: '16px',
                  textAlign: 'center',
                  color: '#ffffff',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  borderBottom: '2px solid #e6e6e6'
                }}>Premium Economy Class Pack</th>
                <th style={{
                  padding: '16px',
                  textAlign: 'center',
                  color: '#ffffff',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  borderBottom: '2px solid #e6e6e6'
                }}>First Class Pack</th>
              </tr>
            </thead>
            <tbody>
              <tr style={{
                backgroundColor: '#ffffff'
              }}>
                <td style={{
                  padding: '12px 16px',
                  color: '#333',
                  fontSize: '14px',
                  borderBottom: '1px solid #e6e6e6'
                }}>Daily Features Access</td>
                <td style={{
                  padding: '12px 16px',
                  textAlign: 'center',
                  color: '#666',
                  fontSize: '14px',
                  borderBottom: '1px solid #e6e6e6'
                }}>
                  3 Uses/Day
                </td>
                <td style={{
                  padding: '12px 16px',
                  textAlign: 'center',
                  color: '#1a9a9a',
                  fontSize: '16px',
                  borderBottom: '1px solid #e6e6e6'
                }}>
                  Unlimited
                </td>
                <td style={{
                  padding: '12px 16px',
                  textAlign: 'center',
                  color: '#1a9a9a',
                  fontSize: '16px',
                  borderBottom: '1px solid #e6e6e6'
                }}>
                  Unlimited
                </td>
              </tr>
              <tr style={{
                backgroundColor: '#f8f9fa'
              }}>
                <td style={{
                  padding: '12px 16px',
                  color: '#333',
                  fontSize: '14px',
                  borderBottom: '1px solid #e6e6e6'
                }}>
                  AI Personalized Lists
                </td>
                <td style={{
                  padding: '12px 16px',
                  textAlign: 'center',
                  color: '#999',
                  fontSize: '16px',
                  borderBottom: '1px solid #e6e6e6'
                }}>
                  -
                </td>
                <td style={{
                  padding: '12px 16px',
                  textAlign: 'center',
                  color: '#1a9a9a',
                  fontSize: '16px',
                  borderBottom: '1px solid #e6e6e6'
                }}>
                  ✓
                </td>
                <td style={{
                  padding: '12px 16px',
                  textAlign: 'center',
                  color: '#1a9a9a',
                  fontSize: '16px',
                  borderBottom: '1px solid #e6e6e6'
                }}>
                  ✓
                </td>
              </tr>
              <tr style={{
                backgroundColor: '#ffffff'
              }}>
                <td style={{
                  padding: '12px 16px',
                  color: '#333',
                  fontSize: '14px',
                  borderBottom: '1px solid #e6e6e6'
                }}>
                  Health & Wellness Preparation
                </td>
                <td style={{
                  padding: '12px 16px',
                  textAlign: 'center',
                  color: '#999',
                  fontSize: '16px',
                  borderBottom: '1px solid #e6e6e6'
                }}>
                  -
                </td>
                <td style={{
                  padding: '12px 16px',
                  textAlign: 'center',
                  color: '#1a9a9a',
                  fontSize: '16px',
                  borderBottom: '1px solid #e6e6e6'
                }}>
                  ✓
                </td>
                <td style={{
                  padding: '12px 16px',
                  textAlign: 'center',
                  color: '#1a9a9a',
                  fontSize: '16px',
                  borderBottom: '1px solid #e6e6e6'
                }}>
                  ✓
                </td>
              </tr>
              <tr style={{
                backgroundColor: '#f8f9fa'
              }}>
                <td style={{
                  padding: '12px 16px',
                  color: '#333',
                  fontSize: '14px',
                  borderBottom: '1px solid #e6e6e6'
                }}>
                  Offline Mode
                </td>
                <td style={{
                  padding: '12px 16px',
                  textAlign: 'center',
                  color: '#999',
                  fontSize: '16px',
                  borderBottom: '1px solid #e6e6e6'
                }}>
                  -
                </td>
                <td style={{
                  padding: '12px 16px',
                  textAlign: 'center',
                  color: '#999',
                  fontSize: '16px',
                  borderBottom: '1px solid #e6e6e6'
                }}>
                  -
                </td>
                <td style={{
                  padding: '12px 16px',
                  textAlign: 'center',
                  color: '#1a9a9a',
                  fontSize: '16px',
                  borderBottom: '1px solid #e6e6e6'
                }}>
                  ✓
                </td>
              </tr>
              <tr style={{
                backgroundColor: '#ffffff'
              }}>
                <td style={{
                  padding: '12px 16px',
                  color: '#333',
                  fontSize: '14px'
                }}>
                  Multi-City & Travel Analytics
                </td>
                <td style={{
                  padding: '12px 16px',
                  textAlign: 'center',
                  color: '#999',
                  fontSize: '16px'
                }}>
                  -
                </td>
                <td style={{
                  padding: '12px 16px',
                  textAlign: 'center',
                  color: '#999',
                  fontSize: '16px'
                }}>
                  -
                </td>
                <td style={{
                  padding: '12px 16px',
                  textAlign: 'center',
                  color: '#1a9a9a',
                  fontSize: '16px'
                }}>
                  ✓
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="email-pricing-box" style={{
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          padding: '20px',
          margin: '24px 0'
        }}>
          <h3 className="email-feature-heading" style={{
            color: '#1a9a9a',
            fontSize: '20px',
            fontWeight: 'bold',
            margin: '0 0 16px'
          }}>Premium Economy Class Pack Boost Passes:</h3>
          
          <div 
            onClick={() => isPreview && handleBoostPass('premium_economy', '3', 3.49)}
            style={{
              marginBottom: '16px',
              cursor: isPreview ? 'pointer' : 'default',
              padding: '12px',
              borderRadius: '6px',
              border: '2px solid transparent',
              backgroundColor: isPreview ? '#f8f9fa' : 'transparent',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => isPreview && (e.currentTarget.style.borderColor = '#1a9a9a')}
            onMouseLeave={(e) => isPreview && (e.currentTarget.style.borderColor = 'transparent')}
          >
            <p style={{
              color: '#333',
              fontSize: '16px',
              fontWeight: 'bold',
              margin: '0 0 8px'
            }}>
              3-Day Pass - €3.49 {isPreview && '→'}
            </p>
            <p style={{
              color: '#666',
              fontSize: '14px',
              lineHeight: '20px',
              margin: '0'
            }}>Quick Access for Urgent Trip Planning</p>
          </div>
          
          <div 
            onClick={() => isPreview && handleBoostPass('premium_economy', '7', 5.99)}
            style={{
              marginBottom: '16px',
              cursor: isPreview ? 'pointer' : 'default',
              padding: '12px',
              borderRadius: '6px',
              border: '2px solid transparent',
              backgroundColor: isPreview ? '#f8f9fa' : 'transparent',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => isPreview && (e.currentTarget.style.borderColor = '#1a9a9a')}
            onMouseLeave={(e) => isPreview && (e.currentTarget.style.borderColor = 'transparent')}
          >
            <p style={{
              color: '#333',
              fontSize: '16px',
              fontWeight: 'bold',
              margin: '0 0 8px'
            }}>
              7-Day Pass - €5.99 {isPreview && '→'}
            </p>
            <p style={{
              color: '#666',
              fontSize: '14px',
              lineHeight: '20px',
              margin: '0'
            }}>Perfect for Week-long Adventures</p>
          </div>
          
          <div 
            onClick={() => isPreview && handleBoostPass('premium_economy', '14', 9.99)}
            style={{
              marginBottom: '16px',
              cursor: isPreview ? 'pointer' : 'default',
              padding: '12px',
              borderRadius: '6px',
              border: '2px solid transparent',
              backgroundColor: isPreview ? '#f8f9fa' : 'transparent',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => isPreview && (e.currentTarget.style.borderColor = '#1a9a9a')}
            onMouseLeave={(e) => isPreview && (e.currentTarget.style.borderColor = 'transparent')}
          >
            <p style={{
              color: '#333',
              fontSize: '16px',
              fontWeight: 'bold',
              margin: '0 0 8px'
            }}>
              14-Day Pass - €9.99 {isPreview && '→'}
            </p>
            <p style={{
              color: '#666',
              fontSize: '14px',
              lineHeight: '20px',
              margin: '0'
            }}>Just €0.71/day — lowest price per day</p>
          </div>

          <h3 style={{
            color: '#1a9a9a',
            fontSize: '20px',
            fontWeight: 'bold',
            margin: '20px 0 16px'
          }}>First Class Pack Boost Passes:</h3>
          
          <div 
            onClick={() => isPreview && handleBoostPass('first_class', '3', 5.99)}
            style={{
              marginBottom: '16px',
              cursor: isPreview ? 'pointer' : 'default',
              padding: '12px',
              borderRadius: '6px',
              border: '2px solid transparent',
              backgroundColor: isPreview ? '#f8f9fa' : 'transparent',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => isPreview && (e.currentTarget.style.borderColor = '#f0d763')}
            onMouseLeave={(e) => isPreview && (e.currentTarget.style.borderColor = 'transparent')}
          >
            <p style={{
              color: '#333',
              fontSize: '16px',
              fontWeight: 'bold',
              margin: '0 0 8px'
            }}>
              3-Day Pass - €5.99 {isPreview && '→'}
            </p>
            <p style={{
              color: '#666',
              fontSize: '14px',
              lineHeight: '20px',
              margin: '0'
            }}>Premium Access for Short Trips</p>
          </div>
          
          <div 
            onClick={() => isPreview && handleBoostPass('first_class', '7', 10.99)}
            style={{
              marginBottom: '16px',
              cursor: isPreview ? 'pointer' : 'default',
              padding: '12px',
              borderRadius: '6px',
              border: '2px solid transparent',
              backgroundColor: isPreview ? '#f8f9fa' : 'transparent',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => isPreview && (e.currentTarget.style.borderColor = '#f0d763')}
            onMouseLeave={(e) => isPreview && (e.currentTarget.style.borderColor = 'transparent')}
          >
            <p style={{
              color: '#333',
              fontSize: '16px',
              fontWeight: 'bold',
              margin: '0 0 8px'
            }}>
              7-Day Pass - €10.99 {isPreview && '→'}
            </p>
            <p style={{
              color: '#666',
              fontSize: '14px',
              lineHeight: '20px',
              margin: '0'
            }}>Most Popular Premium Option</p>
          </div>
          
          <div 
            onClick={() => isPreview && handleBoostPass('first_class', '14', 16.99)}
            style={{
              cursor: isPreview ? 'pointer' : 'default',
              padding: '12px',
              borderRadius: '6px',
              border: '2px solid transparent',
              backgroundColor: isPreview ? '#f8f9fa' : 'transparent',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => isPreview && (e.currentTarget.style.borderColor = '#f0d763')}
            onMouseLeave={(e) => isPreview && (e.currentTarget.style.borderColor = 'transparent')}
          >
            <p style={{
              color: '#333',
              fontSize: '16px',
              fontWeight: 'bold',
              margin: '0 0 8px'
            }}>
              14-Day Pass - €16.99 {isPreview && '→'}
            </p>
            <p style={{
              color: '#666',
              fontSize: '14px',
              lineHeight: '20px',
              margin: '0'
            }}>Just €1.21/day — lowest price per day</p>
          </div>
        </div>

        <a href="/pricing" className="email-button" style={{
          backgroundColor: '#f0d763',
          borderRadius: '6px',
          color: '#1a9a9a',
          fontSize: '16px',
          fontWeight: 'bold',
          textDecoration: 'none',
          textAlign: 'center',
          display: 'block',
          padding: '14px 24px',
          margin: '24px 0'
        }}>
          Get Your Boost Pass Now
        </a>

        <hr style={{
          borderColor: '#e6e6e6',
          margin: '32px 0'
        }} />

        <p className="email-text" style={{
          color: '#666',
          fontSize: '16px',
          lineHeight: '24px',
          marginTop: '32px'
        }}>
          Happy travels,
          <br />
          The OdyVoy Team 🌟
        </p>

        <hr style={{
          borderColor: '#e6e6e6',
          margin: '32px 0'
        }} />

        <div style={{
          textAlign: 'center',
          margin: '32px 0'
        }}>
          <p className="email-text" style={{
            color: '#333',
            fontSize: '16px',
            fontWeight: 'bold',
            marginBottom: '16px'
          }}>
            Stay Connected With Us
          </p>
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '20px',
            flexWrap: 'wrap',
            alignItems: 'center'
          }}>
            <a href="https://www.instagram.com/odyvoy_/" style={{
              textDecoration: 'none',
              color: '#64748b',
              transition: 'color 0.2s'
            }} aria-label="Follow us on Instagram">
              <Instagram className="email-social-icon" size={28} strokeWidth={1.5} />
            </a>
            <a href="https://www.tiktok.com/@odyvoy_" style={{
              textDecoration: 'none',
              color: '#64748b',
              transition: 'color 0.2s'
            }} aria-label="Follow us on TikTok">
              <Music2 className="email-social-icon" size={28} strokeWidth={1.5} />
            </a>
            <a href="https://www.linkedin.com/company/odyvoy/about/?viewAsMember=true" style={{
              textDecoration: 'none',
              color: '#64748b',
              transition: 'color 0.2s'
            }} aria-label="Follow us on LinkedIn">
              <Linkedin className="email-social-icon" size={28} strokeWidth={1.5} />
            </a>
          </div>
          
        </div>

        <div style={{
        textAlign: 'center',
        marginTop: '24px',
        paddingTop: '24px',
        borderTop: '1px solid #e6e6e6'
      }}>
          <p style={{
            color: '#8898aa',
            fontSize: '12px',
            lineHeight: '18px',
            margin: '8px 0'
          }}>
            <strong>OdyVoy</strong>
          </p>
          <p style={{
            color: '#8898aa',
            fontSize: '12px',
            lineHeight: '18px',
            margin: '8px 0'
          }}>Your AI-Powered Travel Companion</p>
          <p style={{
            color: '#8898aa',
            fontSize: '12px',
            lineHeight: '18px',
            margin: '16px 0'
          }}>
            <a href="/unsubscribe" style={{
              color: '#1a9a9a',
              textDecoration: 'underline'
            }}>Unsubscribe</a>
            {' | '}
            <a href="/privacy" style={{
              color: '#1a9a9a',
              textDecoration: 'underline'
            }}>Privacy Policy</a>
            {' | '}
            <a href="/terms" style={{
              color: '#1a9a9a',
              textDecoration: 'underline'
            }}>Terms of Service</a>
          </p>
          <p style={{
            color: '#8898aa',
            fontSize: '11px',
            lineHeight: '16px',
            margin: '16px 0'
          }}>
            © {new Date().getFullYear()} OdyVoy. All rights reserved.
          </p>
        </div>
      </div>
    </div>
    </>;
};
export default BoostPassPromo;